// Connection (link) types with distinct technical line styles. Used in the
// React Flow canvas, the SVG/PDF export, the in-document legend, and draw.io export.

export const CONNECTION_TYPES = [
  { key: 'ethernet', de: 'Ethernet (Kupfer)', en: 'Ethernet (Copper)', color: '#1B232C', width: 1.8, dash: '' },
  { key: 'lwl', de: 'LWL (Glasfaser)', en: 'Fiber (LWL)', color: '#C2410C', width: 2.0, dash: '' },
  { key: 'trunk', de: 'Trunk / Uplink', en: 'Trunk / Uplink', color: '#0B5FFF', width: 2.6, dash: '' },
  { key: 'wan', de: 'WAN', en: 'WAN', color: '#0F7B6C', width: 1.8, dash: '9 5' },
  { key: 'wireless', de: 'WLAN', en: 'Wireless', color: '#7C3AED', width: 1.8, dash: '2 5' },
  { key: 'serial', de: 'Seriell', en: 'Serial', color: '#5B6672', width: 1.6, dash: '10 4 2 4' },
];

export const DEFAULT_CONN = 'ethernet';

export function connType(key) {
  return CONNECTION_TYPES.find((c) => c.key === key) || CONNECTION_TYPES[0];
}

export function connLabel(key, lang = 'de') {
  const c = connType(key);
  return lang === 'en' ? c.en : c.de;
}

// React Flow / DOM inline style
export function connStyle(key, selected) {
  const c = connType(key);
  return {
    stroke: selected ? '#0B5FFF' : c.color,
    strokeWidth: c.width + (selected ? 0.8 : 0),
    strokeDasharray: c.dash || undefined,
  };
}

// SVG attribute string for the export renderer
export function connSvgAttrs(key) {
  const c = connType(key);
  return `stroke="${c.color}" stroke-width="${c.width}"${c.dash ? ` stroke-dasharray="${c.dash}"` : ''}`;
}

// draw.io style fragment
export function connDrawioStyle(key) {
  const c = connType(key);
  const dash = c.dash ? `dashed=1;dashPattern=${c.dash};` : 'dashed=0;';
  return `strokeColor=${c.color};strokeWidth=${c.width};${dash}`;
}

// Group standard-page connections by the exact endpoints they share
// (device + side/handle). Only cables that share BOTH endpoints overlap and so
// must be fanned out onto separate lanes; cables attached to different sides are
// independent and must NOT share a lane budget (doing so previously shifted
// visible lines, hid some, and left uneven gaps). Returns a map
// edgeId -> { index, count } describing each cable's lane within its group.
export function computeParallelLanes(edges = []) {
  const endpointKey = (e) => {
    const a = `${e.source}#${e.sourceSide || ''}`;
    const b = `${e.target}#${e.targetSide || ''}`;
    return [a, b].sort().join('||');
  };
  const groups = {};
  edges.forEach((e) => {
    const k = endpointKey(e);
    (groups[k] = groups[k] || []).push(e.id);
  });
  const lanes = {};
  edges.forEach((e) => {
    const sib = groups[endpointKey(e)] || [e.id];
    lanes[e.id] = { index: sib.indexOf(e.id), count: sib.length };
  });
  return lanes;
}

// Bundle many cables on a busy DEVICE SIDE into a single shared anchor point.
// On standard pages, when more than `maxSeparate` cables dock on the SAME
// (device, side) they are no longer fanned out into individual lines; instead
// they all converge on the side's anchor (visually one line) and a single count
// label is shown there. Grouping is per (deviceId, side) regardless of the peer.
// Returns a map edgeId -> {
//   sourceCollapsed, targetCollapsed,  // is this end part of a bundle?
//   sourceCount, targetCount,          // total cables on that device side
//   sourceLabelOwner, targetLabelOwner // exactly one edge per side renders the label
// }
export function computeSideBundles(edges = [], maxSeparate = 3) {
  const sideKey = (dev, side) => `${dev}#${side || ''}`;
  const groups = {};
  edges.forEach((e) => {
    const sk = sideKey(e.source, e.sourceSide);
    const tk = sideKey(e.target, e.targetSide);
    (groups[sk] = groups[sk] || []).push(e.id);
    (groups[tk] = groups[tk] || []).push(e.id);
  });
  const res = {};
  edges.forEach((e) => {
    const sg = groups[sideKey(e.source, e.sourceSide)] || [e.id];
    const tg = groups[sideKey(e.target, e.targetSide)] || [e.id];
    const sc = sg.length > maxSeparate;
    const tc = tg.length > maxSeparate;
    res[e.id] = {
      sourceCollapsed: sc,
      targetCollapsed: tc,
      sourceCount: sg.length,
      targetCount: tg.length,
      sourceLabelOwner: sc && sg[0] === e.id,
      targetLabelOwner: tc && tg[0] === e.id,
    };
  });
  return res;
}
